import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MoreHorizontal, Radio } from 'lucide-react';
import { useIntegrations, useDisconnectIntegration } from '@/hooks';
import { integrationService } from '@/services';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { confirmDisconnect } from './disconnect-confirm.store';
import { guessOdooDatabase } from '../odoo';
import type { Integration } from '../types';

const odooFormSchema = z.object({
	baseUrl: z.string().trim().min(1, 'Odoo URL is required').max(512, 'Odoo URL is too long'),
	database: z.string().trim().min(1, 'Database is required').max(128, 'Database name is too long'),
	login: z.string().trim().min(1, 'Login is required').max(256, 'Login is too long'),
	apiKey: z.string().trim().min(1, 'API key is required').max(512, 'API key is too long')
});

type OdooFormData = z.infer<typeof odooFormSchema>;

interface OdooConnectError {
	response?: { data?: { error?: string; missingAccess?: string[] } };
}

const GuideStep = ({ step, children }: { step: number; children: ReactNode }) => (
	<li className='flex gap-2'>
		<span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium'>{step}</span>
		<div className='flex flex-1 flex-col gap-1.5 text-xs'>{children}</div>
	</li>
);

/** How to give Kim its own Odoo user and a key that does not expire. */
const ApiKeyGuide = () => (
	<div className='flex flex-col gap-3 rounded-lg border p-3'>
		<p className='text-xs font-medium'>How to get your API key</p>
		<ol className='flex flex-col gap-2.5'>
			<GuideStep step={1}>
				<span>
					In Odoo, go to <strong className='font-medium'>Settings → Users</strong> and create a user for Kim with <strong className='font-medium'>Sales</strong>{' '}
					access (and <strong className='font-medium'>Inventory</strong> for delivery tracking).
				</span>
				<span className='text-[11px] text-muted-foreground'>
					To let Kim change delivery addresses, also tick <strong className='font-medium'>Extra Rights → Contact Creation</strong> for that user.
				</span>
			</GuideStep>
			<GuideStep step={2}>
				<span>
					Signed in as that user, open <strong className='font-medium'>Preferences → Account Security → New API Key</strong>.
				</span>
				<span className='text-[11px] text-muted-foreground'>
					Pick <strong className='font-medium'>Persistent Key</strong> if Odoo offers it (administrators only); other keys expire within 90 days and Kim stops working until a new one is
					connected.
				</span>
			</GuideStep>
			<GuideStep step={3}>
				<span>Copy the key and paste it above, with the user&apos;s login email.</span>
			</GuideStep>
		</ol>
	</div>
);

const ConnectedDetail = ({ label, value }: { label: string; value: unknown }) =>
	typeof value === 'string' && value ? (
		<div className='flex flex-col gap-0.5'>
			<Label className='text-xs text-muted-foreground'>{label}</Label>
			<span className='text-sm font-medium break-all'>{value}</span>
		</div>
	) : null;

export const OdooContent = ({ integration }: { integration: Integration }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [missingAccess, setMissingAccess] = useState<string[]>([]);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<OdooFormData>({
		resolver: zodResolver(odooFormSchema),
		defaultValues: { baseUrl: '', database: '', login: '', apiKey: '' }
	});

	/** Fill the database from an odoo.com address, unless the user already typed one. */
	const handleBaseUrlBlur = () => {
		const guess = guessOdooDatabase(form.getValues('baseUrl'));
		if (guess && !form.getValues('database')) form.setValue('database', guess);
	};

	const handleConnect = async (data: OdooFormData) => {
		setIsSubmitting(true);
		setMissingAccess([]);
		try {
			const { warning } = await integrationService.connectOdooIntegration({
				base_url: data.baseUrl,
				database: data.database,
				login: data.login,
				api_key: data.apiKey
			});
			await refetch();
			if (warning) toast.warning(`Odoo connected. ${warning}`, { duration: 15000 });
			else toast.success('Odoo connected');
		} catch (err) {
			console.error('Odoo connect failed', err);
			const errorResponse = (err as OdooConnectError)?.response?.data;
			setMissingAccess(errorResponse?.missingAccess ?? []);
			toast.error(errorResponse?.error ?? 'Failed to connect Odoo. Check the details and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		const meta = integration.authMetaData ?? {};
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm' aria-label='Odoo connection actions'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-3 p-3 bg-muted rounded-lg'>
					<ConnectedDetail label='Status' value='Connected' />
					<ConnectedDetail label='Odoo URL' value={meta.base_url} />
					<ConnectedDetail label='Database' value={meta.database} />
					<ConnectedDetail label='Signed in as' value={meta.login} />
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link an {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Kim connects as an Odoo user and can only see and change what that user can.</p>
					</div>

					<FormField
						control={form.control}
						name='baseUrl'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Odoo URL</FormLabel>
								<FormControl>
									<Input placeholder='https://yourcompany.odoo.com' {...field} onBlur={() => { field.onBlur(); handleBaseUrlBlur(); }} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='database'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Database</FormLabel>
								<FormControl>
									<Input placeholder='yourcompany' {...field} />
								</FormControl>
								<p className='text-[11px] text-muted-foreground'>
									Filled in from an odoo.com address, but the real name can differ. To check, open Odoo, add{' '}
									<code className='font-mono'>?debug=1</code> to the end of its address and press Enter. The database name shows
									under your name at the top right.
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='login'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Login email</FormLabel>
								<FormControl>
									<Input autoComplete='off' placeholder='kim@yourcompany.com' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter the Odoo API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{missingAccess.length > 0 && (
						<div role='alert' className='rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive'>
							This Odoo user cannot read: {missingAccess.join(', ')}. Give the user Sales access in Odoo and connect again.
						</div>
					)}

					<ApiKeyGuide />
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};
